import { canAccessCrm } from "../../utils/crmAccess";
import { getCrmColors } from "../../theme/crmColors";
import React, { useEffect, useState, useContext, useRef } from "react";
import { Link as RouterLink } from "react-router-dom";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import Drawer from "@material-ui/core/Drawer";
import Link from "@material-ui/core/Link";
import InputLabel from "@material-ui/core/InputLabel";
import Avatar from "@material-ui/core/Avatar";
import PersonOutlineIcon from "@material-ui/icons/PersonOutline";
import Button from "@material-ui/core/Button";
import Paper from "@material-ui/core/Paper";
import CreateIcon from '@material-ui/icons/Create';
import formatSerializedId from '../../utils/formatSerializedId';
import { i18n } from "../../translate/i18n";
import ModalImageCors from "../ModalImageCors"
import ContactDrawerSkeleton from "../ContactDrawerSkeleton";
import MarkdownWrapper from "../MarkdownWrapper";
import { CardHeader, Switch, Tooltip } from "@material-ui/core";
import { ContactForm } from "../ContactForm";
import ContactModal from "../ContactModal";
import { ContactNotes } from "../ContactNotes";
import CommercialContext from "../CommercialContext";
import CommercialRelationshipControls from "../CommercialRelationshipControls";
import CustomerFields from "../CustomerFields";

import { AuthContext } from "../../context/Auth/AuthContext";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { toast } from "react-toastify";
import { TagsKanbanContainer } from "../TagsKanbanContainer";
import { useIndividualContactPhoto } from "../../hooks/useContactPhotoUpdates";


const drawerWidth = 320;

const useStyles = makeStyles(theme => ({
	drawer: {
		position: "absolute",
		top: 0,
		right: 0,
		bottom: 0,
		width: drawerWidth,
		flexShrink: 0,
		zIndex: theme.zIndex.drawer + 2,
		pointerEvents: "none",
		overflow: "visible",
	},
	drawerPaper: {
		width: drawerWidth,
		maxWidth: "calc(100% - 16px)",
		height: "100%",
		position: "absolute",
		top: 0,
		right: 0,
		display: "flex",
		background: theme.ticketDark ? getCrmColors(theme, "dark").surface : (theme.mode === "light" ? "#ffffff" : "#2f2f2f"),
		border: 0,
		borderLeft: theme.ticketDark ? `1px solid ${getCrmColors(theme, "dark").border}` : (theme.mode === "light" ? "1px solid #d9e2ef" : "1px solid rgba(255,255,255,0.08)"),
		boxShadow: theme.mode === "light" ? "-12px 0 28px rgba(15, 23, 42, 0.08)" : "-12px 0 28px rgba(0,0,0,0.22)",
		color: theme.ticketDark ? "#FFFFFF" : (theme.mode === "light" ? "#172033" : "#f8fafc"),
		pointerEvents: "auto",
	},
	header: {
		display: "flex",
		borderBottom: theme.ticketDark ? `1px solid ${getCrmColors(theme, "dark").border}` : (theme.mode === "light" ? "1px solid #e6edf6" : "1px solid rgba(255,255,255,0.08)"),
		backgroundColor: theme.ticketDark ? getCrmColors(theme, "dark").surface : (theme.mode === "light" ? "#ffffff" : "#2f2f2f"),
		alignItems: "center",
		gap: theme.spacing(1),
		padding: theme.spacing(0.75, 1.25),
		minHeight: 58,
		justifyContent: "flex-start",
		"& .MuiIconButton-root": {
			width: 36,
			height: 36,
			color: theme.ticketDark ? "#FFFFFF" : (theme.mode === "light" ? "#64748b" : "#dbeafe"),
			backgroundColor: theme.ticketDark ? getCrmColors(theme, "dark").raised : (theme.mode === "light" ? "#f4f7fb" : "#3a3a3a"),
			"&:hover": {
				backgroundColor: theme.ticketDark ? getCrmColors(theme, "dark").hover : (theme.mode === "light" ? "#edf6ff" : "#424242"),
			},
		},
	},
	headerTitle: {
		fontSize: 15,
		fontWeight: 800,
		color: theme.ticketDark ? "#FFFFFF" : (theme.mode === "light" ? "#172033" : "#f8fafc"),
		letterSpacing: 0,
	},
	audioToggleRow: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1),
		padding: theme.spacing(1, 1.5),
		background: theme.ticketDark ? getCrmColors(theme, "dark").inset : (theme.mode === "light" ? "#f8fbff" : "#343434"),
		borderBottom: theme.ticketDark ? `1px solid ${getCrmColors(theme, "dark").border}` : (theme.mode === "light" ? "1px solid #e6edf6" : "1px solid rgba(255,255,255,0.08)"),
		color: theme.ticketDark ? "#FFFFFF" : (theme.mode === "light" ? "#172033" : "#f8fafc"),
		fontSize: 13,
		"& .MuiTypography-root": {
			fontSize: 13,
			fontWeight: 600,
		},
	},
	content: {
		display: "flex",
		backgroundColor: theme.ticketDark ? getCrmColors(theme, "dark").background : (theme.mode === "light" ? "#f4f7fb" : "#424242"),
		flexDirection: "column",
		gap: theme.spacing(1.25),
		padding: theme.spacing(1.25),
		height: "100%",
		justifyContent: "flex-start",
		overflowY: "auto",
		...theme.scrollbarStyles,
	},

	contactAvatar: {
		margin: 15,
		width: 160,
		height: 160,
		borderRadius: 10,
	},

	contactHeader: {
		display: "flex",
		padding: theme.spacing(1.5),
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		border: theme.ticketDark ? `1px solid ${getCrmColors(theme, "dark").border}` : (theme.mode === "light" ? "1px solid #d9e2ef" : "1px solid rgba(255,255,255,0.08)"),
		borderRadius: 8,
		background: theme.ticketDark ? getCrmColors(theme, "dark").surface : (theme.mode === "light" ? "#ffffff" : "#2f2f2f"),
		boxShadow: theme.mode === "light" ? "0 10px 24px rgba(15, 23, 42, 0.06)" : "0 12px 28px rgba(0,0,0,0.18)",
		"& > *": {
			margin: 4,
		},
		"& img": {
			borderRadius: 8,
			maxHeight: 150,
			objectFit: "cover",
		},
		"& .MuiCardHeader-root": {
			padding: theme.spacing(0.5, 0),
		},
		"& .MuiCardHeader-content": {
			minWidth: 0,
			textAlign: "center",
		},
	},
	contactName: {
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		gap: 4,
		maxWidth: "100%",
		fontSize: 17,
		fontWeight: 800,
		color: theme.ticketDark ? "#FFFFFF" : (theme.mode === "light" ? "#172033" : "#f8fafc"),
		cursor: "pointer",
	},
	contactMeta: {
		fontSize: 12,
		color: theme.ticketDark ? getCrmColors(theme, "dark").muted : (theme.mode === "light" ? "#64748b" : "#cbd5e1"),
	},
	contactLink: {
		color: theme.ticketDark ? getCrmColors(theme, "dark").accent : (theme.mode === "light" ? theme.palette.primary.main : "#9ccaef"),
		fontSize: 12,
	},
	contactActions: {
		display: "flex",
		flexDirection: "column",
		gap: theme.spacing(1),
		width: "100%",
		marginTop: theme.spacing(1),
	},
	editContactButton: {
		borderRadius: 6,
		fontWeight: 800,
		textTransform: "uppercase",
		boxShadow: theme.mode === "light" ? "0 8px 18px rgba(93, 170, 252, 0.16)" : "0 8px 18px rgba(0,0,0,0.18)",
	},
	blockButton: {
		borderRadius: 6,
		fontWeight: 800,
		color: theme.ticketDark ? "#FF8B96" : "#ff3860",
		borderColor: theme.mode === "light" ? "#ffc1cf" : "rgba(255, 56, 96, 0.34)",
		backgroundColor: theme.mode === "light" ? "#fff6f8" : "rgba(255, 56, 96, 0.08)",
		"&:hover": {
			borderColor: "#ff3860",
			backgroundColor: theme.mode === "light" ? "#ffe8ee" : "rgba(255, 56, 96, 0.14)",
		},
	},

	contactDetails: {
		padding: theme.spacing(1.25),
		display: "flex",
		flexDirection: "column",
		border: theme.ticketDark ? `1px solid ${getCrmColors(theme, "dark").border}` : (theme.mode === "light" ? "1px solid #d9e2ef" : "1px solid rgba(255,255,255,0.08)"),
		borderRadius: 8,
		background: theme.ticketDark ? getCrmColors(theme, "dark").surface : (theme.mode === "light" ? "#ffffff" : "#2f2f2f"),
		boxShadow: theme.mode === "light" ? "0 8px 18px rgba(15, 23, 42, 0.04)" : "0 10px 24px rgba(0,0,0,0.14)",
		color: theme.ticketDark ? "#FFFFFF" : (theme.mode === "light" ? "#172033" : "#f8fafc"),
		"& .MuiInputBase-root": {
			backgroundColor: theme.ticketDark ? getCrmColors(theme, "dark").inset : (theme.mode === "light" ? "#ffffff" : "#343434"),
			color: theme.ticketDark ? "#FFFFFF" : (theme.mode === "light" ? "#172033" : "#f8fafc"),
		},
		"& .MuiOutlinedInput-notchedOutline": {
			borderColor: theme.ticketDark ? getCrmColors(theme, "dark").border : (theme.mode === "light" ? "#d7e1ee" : "rgba(255,255,255,0.12)"),
		},
	},
	sectionTitle: {
		marginBottom: theme.spacing(1),
		fontSize: 14,
		fontWeight: 800,
		color: theme.ticketDark ? "#FFFFFF" : (theme.mode === "light" ? "#172033" : "#f8fafc"),
	},
	contactExtraInfo: {
		marginTop: theme.spacing(0.75),
		padding: theme.spacing(1),
		border: theme.ticketDark ? `1px solid ${getCrmColors(theme, "dark").border}` : (theme.mode === "light" ? "1px solid #e6edf6" : "1px solid rgba(255,255,255,0.08)"),
		borderRadius: 6,
		background: theme.ticketDark ? getCrmColors(theme, "dark").inset : (theme.mode === "light" ? "#f8fbff" : "#343434"),
		"& .MuiInputLabel-root": {
			color: theme.ticketDark ? getCrmColors(theme, "dark").muted : (theme.mode === "light" ? "#64748b" : "#cbd5e1"),
			fontSize: 12,
			fontWeight: 700,
		},
		"& .MuiTypography-root": {
			color: theme.ticketDark ? "#FFFFFF" : (theme.mode === "light" ? "#172033" : "#f8fafc"),
		},
	},
}));

const ContactDrawer = ({ open, handleDrawerClose, contact, ticket, loading }) => {
	const classes = useStyles();
	const theme = useTheme();
	const usePlaceholderAvatar = theme.ticketDark && (!contact?.urlPicture || /(?:^|\/)nopicture\.png(?:[?#]|$)/.test(contact.urlPicture));

	const [modalOpen, setModalOpen] = useState(false);
	const [blockingContact, setBlockingContact] = useState(contact.active);
	const [openForm, setOpenForm] = useState(false);
	const formContact = useRef(contact);
	if (!openForm || formContact.current?.id !== contact?.id) formContact.current = contact;
	const { get } = useCompanySettings();
	const [hideNum, setHideNum] = useState(false);
	const { user } = useContext(AuthContext);
	const onPhotoError = useIndividualContactPhoto({ contact, companyId: user.companyId, enabled: open && !loading });
    const [acceptAudioMessage, setAcceptAudio] = useState(contact.acceptAudioMessage);

	useEffect(() => {
		async function fetchData() {

			const lgpdHideNumber = await get({
				"column": "lgpdHideNumber"
			});

			if (lgpdHideNumber === "enabled") setHideNum(true);

		}
		fetchData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		setOpenForm(false);
	}, [open, contact.id]);

	

	const handleContactToggleAcceptAudio = async () => {
        try {
            const contact = await api.put(`/contacts/toggleAcceptAudio/${ticket.contact.id}`);
            setAcceptAudio(contact.data.acceptAudioMessage);
        } catch (err) {
            toastError(err);
        }
    };

	const handleBlockContact = async (contactId) => {
		try {
			await api.put(`/contacts/block/${contactId}`, { active: false });
			toast.success("Contato bloqueado");
		} catch (err) {
			toastError(err);
		}

		setBlockingContact(true);
	};

	const handleUnBlockContact = async (contactId) => {
		try {
			await api.put(`/contacts/block/${contactId}`, { active: true });
			toast.success("Contato desbloqueado");
		} catch (err) {
			toastError(err);
		}
		setBlockingContact(false);
	};

	if (loading) return null;

	return (
		<>
			<Drawer
				className={classes.drawer}
				variant="persistent"
				anchor="right"
				open={open}
				PaperProps={{ style: { position: "absolute", right: 0, top: 0, height: "100%" } }}
				BackdropProps={{ style: { position: "absolute" } }}
				ModalProps={{
					container: document.getElementById("drawer-container"),
					style: { position: "absolute" },
				}}
				classes={{
					paper: classes.drawerPaper,
				}}
			>
				<div className={classes.header}>
					<Tooltip title="Fechar">
						<IconButton onClick={handleDrawerClose} size="small">
							<CloseIcon />
						</IconButton>
					</Tooltip>
					<Typography className={classes.headerTitle}>
						{i18n.t("contactDrawer.header")}
					</Typography>
				</div>
				<div className={classes.audioToggleRow}>
					{!loading ? (
						<>
							<Switch
								size="small"
								checked={acceptAudioMessage}
								onChange={() => handleContactToggleAcceptAudio()}
								name="disableBot"
								color="primary"
							/>
							<Typography>
								{i18n.t("ticketOptionsMenu.acceptAudioMessage")}								
							</Typography>
						</>
					) : (<br />)}
				</div>
				{loading ? (
					<ContactDrawerSkeleton classes={classes} />
				) : (
					<div className={classes.content}>
						<Paper elevation={0} className={classes.contactHeader}>
							{usePlaceholderAvatar ? <Avatar className={classes.contactAvatar} aria-label="Contato sem foto"><PersonOutlineIcon style={{ fontSize: 80 }} /></Avatar> : <ModalImageCors imageUrl={contact?.urlPicture} onError={onPhotoError} />}
							<CardHeader
								onClick={() => { }}
								style={{ cursor: "pointer", width: '100%' }}
								titleTypographyProps={{ noWrap: true }}
								subheaderTypographyProps={{ noWrap: true }}
								title={
									<>
										<Typography className={classes.contactName} onClick={() => setOpenForm(true)} noWrap>
											{contact.name}
											<CreateIcon style={{ fontSize: 16, marginLeft: 5 }} />
										</Typography>
									</>
								}
								subheader={
									<>
										<Typography className={classes.contactMeta}>
											{hideNum && user.profile === "user" ? formatSerializedId(contact.number).slice(0, -6) + "**-**" + contact.number.slice(-2) : formatSerializedId(contact.number)}
										</Typography>
										<Typography>
											<Link className={classes.contactLink} href={`mailto:${contact.email}`}>{contact.email}</Link>
										</Typography>
									</>
								}
							/>
							<div className={classes.contactActions}>
								{canAccessCrm(user) && <Button variant="outlined" color="primary" component={RouterLink} to={`/crm/relationship/${contact.id}`}>
									Ver relacionamento
								</Button>}
								<Button
									variant="contained"
									color="primary"
									onClick={() => setModalOpen(!openForm)}
									className={classes.editContactButton}
								>
									{i18n.t("contactDrawer.buttons.edit")}
								</Button>
								<Button
									variant="outlined"
									onClick={() => contact.active
										? handleBlockContact(contact.id)
										: handleUnBlockContact(contact.id)}
									disabled={loading}
									className={classes.blockButton}
								>
									{!contact.active ? "Desbloquear contato" : "Bloquear contato"}
								</Button>
							</div>
							{(contact.id && openForm) && <ContactForm initialContact={formContact.current} onCancel={() => setOpenForm(false)} />}
						</Paper>
						{canAccessCrm(user) && <>
						<TagsKanbanContainer ticket={ticket} enabled={open} className={classes.contactTags} />
						<CommercialContext contactId={contact.id} contactName={contact.name} enabled={open} hideNumber={hideNum && user.profile === "user"} />
						<CommercialRelationshipControls contactId={contact.id} contactName={contact.name} enabled={open} />
						<CustomerFields contactId={contact.id} enabled={open} />
						</>}
						<Paper elevation={0} className={classes.contactDetails}>
							<Typography className={classes.sectionTitle}>
								{i18n.t("ticketOptionsMenu.appointmentsModal.title")}
							</Typography>
							<ContactNotes ticket={ticket} />
						</Paper>
						<Paper elevation={0} className={classes.contactDetails}>
							<ContactModal
								open={modalOpen}
								onClose={() => setModalOpen(false)}
								contactId={contact.id}
							></ContactModal>
							<Typography className={classes.sectionTitle}>
								{i18n.t("contactDrawer.extraInfo")}
							</Typography>
							{contact?.extraInfo?.map(info => (
								<Paper
									key={info.id}
									elevation={0}
									className={classes.contactExtraInfo}
								>
									<InputLabel>{info.name}</InputLabel>
									<Typography component="div" noWrap style={{ paddingTop: 2 }}>
										<MarkdownWrapper>{info.value}</MarkdownWrapper>
									</Typography>
								</Paper>
							))}
						</Paper>
					</div>
				)}
			</Drawer>
		</>
	);
};

export default ContactDrawer;
